/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * Aggiornamento: Linea Guida AIB n.5 CFRS Sicilia (2022-2024)
 * - Aggiunta azione inviaCoordsDroneACOAU (Linea 5B)
 *   Trasferisce le coordinate GPS rilevate dal drone al form COAU
 *   eliminando la trascrizione manuale (fonte di errori operativi)
 */
// src/store/useAppStore.ts
// ============================================
// DOS518 - Zustand Store - COMPLETO CON LOG
// State Management Centralizzato
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { exportImportManager } from '../utils/exportImportManager';
import type {
  AppState,
  COAU,
  Coordinate,
  SquadLeader,
  Personale,
  MezzoTerrestre,
  MezzoAereo,
  Drone,
  LACES,
  Vedetta,
  MessaggioRadio,
  MediaFile,
} from '../types';

// ========================================
// TYPES LOG COMUNICAZIONI
// ========================================

export interface ComunicazioneLog {
  id: string;
  timestamp: string;
  data: string;
  tipo: 'operativa' | 'sicurezza' | 'avvicinamento';
  mittente: string;
  destinatario: string;
  messaggio: string;
  status?: 'inviata' | 'ricevuta' | 'confermata';
  priorita?: 'normale' | 'urgente' | 'mayday';
}

// ================================================================
// DIARIO OPERATIVO
// Registra automaticamente ogni evento significativo durante
// l'intervento AIB: attivazione risorse, coordinate drone,
// cambio DOS, comunicazioni, note manuali.
// Alla fine dell'operazione diventa il verbale ufficiale.
// ================================================================

export type TipoVoceDiario =
  | 'sistema'        // attivazione/chiusura DOS518
  | 'coau'           // compilazione/modifica COAU
  | 'personale'      // aggiunta/rimozione operatori
  | 'mezzo_terrestre'
  | 'mezzo_aereo'
  | 'drone'          // attivazione drone, GPS rilevato
  | 'coordinate'     // coordinate inviate al COAU
  | 'laces'          // verifica L.A.C.E.S.
  | 'comunicazione'  // log radio
  | 'cambio_dos'     // passaggio consegna
  | 'nota_manuale'   // annotazione libera del DOS
  | 'astuto'         // import/export ASTUTO
  | 'allerta';       // eventi critici

export interface VoceDiario {
  id: string;
  timestamp: string;        // ISO — generato automaticamente
  ora: string;              // HH:MM — per visualizzazione rapida
  tipo: TipoVoceDiario;
  icona: string;            // emoji per riconoscimento rapido
  testo: string;            // descrizione evento
  dosAttivo?: string;       // nome DOS che ha generato l'evento
  dati?: Record<string, any>; // payload opzionale per dettagli
}

// ========================================
// EXTENDED APP STATE
// ========================================

interface ExtendedAppState extends AppState {
  logComunicazioni: ComunicazioneLog[];
  addLogComunicazione: (log: Omit<ComunicazioneLog, 'id' | 'timestamp' | 'data'>) => void;
  clearLogComunicazioni: () => void;
  // Diario Operativo
  diarioOperativo: VoceDiario[];
  dosAttivo: string;          // nome DOS corrente
  oraAttivazione: string;     // ora apertura intervento
  addVoceDiario: (tipo: TipoVoceDiario, icona: string, testo: string, dati?: Record<string, any>) => void;
  addNotaManuale: (testo: string) => void;
  cambioDOS: (nuovoDos: string, notaBriefing?: string) => void;
  clearDiario: () => void;
  // Export/Import avanzato
  exportDataComplete: () => Promise<void>;
  importDataFile: (file: File) => Promise<void>;
  // Posizione DOS
  aggiornaPosizioneDoS: (lat: number, lng: number) => void;
}

// Helper per creare voce diario con timestamp automatico
const creaVoce = (
  tipo: TipoVoceDiario,
  icona: string,
  testo: string,
  dosAttivo = '',
  dati?: Record<string, any>
): VoceDiario => {
  const now = new Date();
  return {
    id: Math.random().toString(36).slice(2),
    timestamp: now.toISOString(),
    ora: now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
    tipo,
    icona,
    testo,
    dosAttivo,
    dati,
  };
};

const initialState = {
  coau: null,
  coordinate: [],
  squadLeaders: [],
  personale: [],
  mezziTerrestri: [],
  mezziAerei: [],
  droni: [],
  lookouts: [] as import('../types').Lookout[],
  laces: null,
  messaggiRadio: [],
  media: [],
  logComunicazioni: [],
  diarioOperativo: [] as VoceDiario[],
  dosAttivo: '',
  oraAttivazione: '',
};

export const useAppStore = create<ExtendedAppState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // COAU
      setCOAU: (coau: COAU) => set({ coau }),
      updateCOAU: (updates: Partial<COAU>) => {
        const state = get();
        const campi = Object.keys(updates).filter(k => !['id','data'].includes(k)).slice(0, 3).join(', ');
        const voce = creaVoce('coau', '📋', `COAU: ${campi || 'aggiornato'}`, state.dosAttivo, updates as any);
        set((s) => ({
          coau: { ...(s.coau || {}), ...updates } as COAU,
          diarioOperativo: [...s.diarioOperativo, voce],
        }));
      },

      // Coordinate
      addCoordinate: (coord: Omit<Coordinate, 'id'>) => {
        const newCoord = { 
          ...coord, 
          id: uuidv4(),
          timestamp: coord.timestamp || Date.now()
        };
        set((state) => ({
          coordinate: [...state.coordinate, newCoord]
        }));
      },
      updateCoordinate: (id: string, updates: Partial<Coordinate>) =>
        set((state) => ({
          coordinate: state.coordinate.map((c) =>
            c.id === id ? { ...c, ...updates } : c
          ),
        })),
      deleteCoordinate: (id: string) =>
        set((state) => ({
          coordinate: state.coordinate.filter((c) => c.id !== id),
        })),

      aggiornaPosizioneDoS: (lat: number, lng: number) => {
        const ora = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        set((state) => {
          const dosIdx = state.coordinate.findIndex(
            (c) => (c as any).titolo === 'Posizione DOS' || c.name === 'Posizione DOS'
          );
          if (dosIdx >= 0) {
            const updated = state.coordinate.map((c, i) =>
              i === dosIdx
                ? { ...c, lat: lat.toFixed(6), lon: lng.toFixed(6), timestamp: ora }
                : c
            );
            return { coordinate: updated };
          }
          const nuova = {
            id: uuidv4(),
            lat: lat.toFixed(6),
            lon: lng.toFixed(6),
            active: true,
            titolo: 'Posizione DOS',
            name: 'Posizione DOS',
            note: '',
            tipo: 'altro' as const,
            type: 'altro' as const,
            capoSquadra: '',
            timestamp: ora,
            timestampAssegnazione: '',
          } as any;
          return { coordinate: [nuova, ...state.coordinate] };
        });
      },

      // Squad Leaders
      addSquadLeader: (leader: Omit<SquadLeader, 'id'>) =>
        set((state) => ({
          squadLeaders: [...state.squadLeaders, { ...leader, id: uuidv4() }],
        })),
      updateSquadLeader: (id: string, updates: Partial<SquadLeader>) =>
        set((state) => ({
          squadLeaders: state.squadLeaders.map((l) =>
            l.id === id ? { ...l, ...updates } : l
          ),
        })),
      deleteSquadLeader: (id: string) =>
        set((state) => ({
          squadLeaders: state.squadLeaders.filter((l) => l.id !== id),
        })),

      // Personale
      addPersonale: (p: Omit<Personale, 'id'>) => {
        const state = get();
        const nuovo = { ...p, id: uuidv4() };
        const voce = creaVoce('personale', '👤', `Personale aggiunto: ${p.cognome} ${p.nome} (${p.qualifica || 'operatore'})`, state.dosAttivo);
        set((s) => ({
          personale: [...s.personale, nuovo],
          diarioOperativo: [...s.diarioOperativo, voce],
        }));
      },
      updatePersonale: (id: string, updates: Partial<Personale>) =>
        set((state) => ({
          personale: state.personale.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          ),
        })),
      deletePersonale: (id: string) =>
        set((state) => ({
          personale: state.personale.filter((p) => p.id !== id),
        })),

      // Mezzi Terrestri
      addMezzoTerrestre: (m: Omit<MezzoTerrestre, 'id'>) => {
        const state = get();
        const nuovo = { ...m, id: uuidv4() };
        const voce = creaVoce('mezzo_terrestre', '🚒', `Mezzo terrestre: ${m.sigla} (${m.tipo || 'tipo n.d.'})`, state.dosAttivo);
        set((s) => ({
          mezziTerrestri: [...s.mezziTerrestri, nuovo],
          diarioOperativo: [...s.diarioOperativo, voce],
        }));
      },
      updateMezzoTerrestre: (id: string, updates: Partial<MezzoTerrestre>) =>
        set((state) => ({
          mezziTerrestri: state.mezziTerrestri.map((m) =>
            m.id === id ? { ...m, ...updates } : m
          ),
        })),
      deleteMezzoTerrestre: (id: string) =>
        set((state) => ({
          mezziTerrestri: state.mezziTerrestri.filter((m) => m.id !== id),
        })),

      // Mezzi Aerei
      addMezzoAereo: (m: Omit<MezzoAereo, 'id'>) => {
        const state = get();
        const nuovo = { ...m, id: uuidv4() };
        const voce = creaVoce('mezzo_aereo', '🚁', `Mezzo aereo: ${m.sigla} (${m.tipo || 'tipo n.d.'})`, state.dosAttivo);
        set((s) => ({
          mezziAerei: [...s.mezziAerei, nuovo],
          diarioOperativo: [...s.diarioOperativo, voce],
        }));
      },
      updateMezzoAereo: (id: string, updates: Partial<MezzoAereo>) =>
        set((state) => ({
          mezziAerei: state.mezziAerei.map((m) =>
            m.id === id ? { ...m, ...updates } : m
          ),
        })),
      deleteMezzoAereo: (id: string) =>
        set((state) => ({
          mezziAerei: state.mezziAerei.filter((m) => m.id !== id),
        })),

      // Droni
      addDrone: (d: Omit<Drone, 'id'>) => {
        const state = get();
        const nuovo = { ...d, id: uuidv4() };
        const voce = creaVoce('drone', '🛸', `Drone attivato: ${d.modello || d.operatore} — missione: ${(d as any).missione || 'n.d.'}`, state.dosAttivo);
        set((s) => ({
          droni: [...s.droni, nuovo],
          diarioOperativo: [...s.diarioOperativo, voce],
        }));
      },
      updateDrone: (id: string, updates: Partial<Drone>) =>
        set((state) => ({
          droni: state.droni.map((d) =>
            d.id === id ? { ...d, ...updates } : d
          ),
        })),
      deleteDrone: (id: string) =>
        set((state) => ({
          droni: state.droni.filter((d) => d.id !== id),
        })),

      // ================================================================
      // LINEA 5B — Invia coordinate GPS drone al COAU
      // Quando il drone geolocalizza il punto fuoco, il DOS preme il
      // pulsante nella card del drone e le coordinate vengono scritte
      // automaticamente nel form COAU (campo latitudine/longitudine),
      // eliminando la trascrizione manuale e riducendo gli errori.
      // ================================================================
      inviaCoordsDroneACOAU: (droneId: string) => {
        const state = get();
        const drone = state.droni.find((d) => d.id === droneId);

        if (!drone?.coordsRilevate) {
          console.warn('Drone senza coordinate rilevate — inserire coordsRilevate prima');
          return;
        }

        // Formato atteso: "lat,lon" (es. "37.502669,14.052149")
        const parts = drone.coordsRilevate.split(',').map((s) => s.trim());
        if (parts.length !== 2) {
          console.warn('Formato coordinate non valido. Usare "lat,lon"');
          return;
        }

        const [lat, lon] = parts;

        set((state) => ({
          coau: {
            ...(state.coau || { id: uuidv4(), data: new Date().toISOString() }),
            latitudine: lat,
            longitudine: lon,
            // Tracciabilità: conserva riferimento al drone sorgente
            coordsDaRone: drone.modello,
            coordsOra: drone.oraRilevamento || new Date().toLocaleTimeString('it-IT'),
          } as COAU,
        }));

        const voce = creaVoce(
          'coordinate', '📍',
          `GPS drone → COAU: ${lat}, ${lon} (${drone.modello || 'drone'})`,
          get().dosAttivo,
          { lat, lon, drone: drone.modello }
        );
        set((s) => ({ diarioOperativo: [...s.diarioOperativo, voce] }));
        console.log(`✅ Coordinate drone ${drone.modello} inviate al COAU: ${lat}, ${lon}`);
      },

      // Lookouts
      addLookout: (lookout: Omit<import('../types').Lookout, 'id' | 'timestamp'>) =>
        set((state) => ({
          lookouts: [
            ...state.lookouts,
            {
              ...lookout,
              id: Date.now(),
              timestamp: new Date().toISOString(),
            },
          ],
        })),
      updateLookout: (id: number, updates: Partial<import('../types').Lookout>) =>
        set((state) => ({
          lookouts: state.lookouts.map((l) =>
            l.id === id ? { ...l, ...updates } : l
          ),
        })),
      deleteLookout: (id: number) =>
        set((state) => ({
          lookouts: state.lookouts.filter((l) => l.id !== id),
        })),

      // LACES
      setLACES: (laces: LACES) => set({ laces }),
      addVedetta: (v: Omit<Vedetta, 'id'>) =>
        set((state) => ({
          laces: state.laces
            ? {
                ...state.laces,
                vedette: [...state.laces.vedette, { ...v, id: uuidv4() }] as Vedetta[],
              }
            : {
                vedette: [{ ...v, id: uuidv4() }] as Vedetta[],
                vieAncora: [],
                zoneSicurezza: [],
                comunicazioni: true,
                ultimaVerifica: new Date(),
              },
        })),
      updateVedetta: (id: string, updates: Partial<Vedetta>) =>
        set((state) => ({
          laces: state.laces
            ? {
                ...state.laces,
                vedette: state.laces.vedette.map((v) =>
                  v.id === id ? { ...v, ...updates } as Vedetta : v
                ),
              }
            : null,
        })),
      deleteVedetta: (id: string) =>
        set((state) => ({
          laces: state.laces
            ? {
                ...state.laces,
                vedette: state.laces.vedette.filter((v) => v.id !== id),
              }
            : null,
        })),

      // Messaggi Radio
      addMessaggio: (m: Omit<MessaggioRadio, 'id'>) =>
        set((state) => ({
          messaggiRadio: [...state.messaggiRadio, { ...m, id: uuidv4() }],
        })),

      // Media
      addMedia: (m: Omit<MediaFile, 'id'>) =>
        set((state) => ({
          media: [...state.media, { ...m, id: uuidv4() }],
        })),
      deleteMedia: (id: string) =>
        set((state) => ({
          media: state.media.filter((m) => m.id !== id),
        })),

      // ========================================
      // LOG COMUNICAZIONI
      // ========================================
      addLogComunicazione: (log) => {
        const now = new Date();
        const timestamp = now.toLocaleTimeString('it-IT', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        });
        const data = now.toLocaleDateString('it-IT');
        const newLog: ComunicazioneLog = {
          id: uuidv4(),
          timestamp,
          data,
          status: 'inviata',
          ...log
        };
        set(state => ({
          logComunicazioni: [...state.logComunicazioni, newLog]
        }));
      },
      clearLogComunicazioni: () => {
        set({ logComunicazioni: [] });
      },

      // ================================================================
      // DIARIO OPERATIVO
      // ================================================================

      addVoceDiario: (tipo, icona, testo, dati) => {
        const state = get();
        const voce = creaVoce(tipo, icona, testo, state.dosAttivo, dati);
        set((s) => ({ diarioOperativo: [...s.diarioOperativo, voce] }));
      },

      addNotaManuale: (testo: string) => {
        const state = get();
        const voce = creaVoce('nota_manuale', '📝', testo, state.dosAttivo);
        set((s) => ({ diarioOperativo: [...s.diarioOperativo, voce] }));
      },

      cambioDOS: (nuovoDos: string, notaBriefing?: string) => {
        const state = get();
        const vecchioDos = state.dosAttivo;
        const voce = creaVoce(
          'cambio_dos', '🔄',
          `Cambio DOS: ${vecchioDos || '—'} → ${nuovoDos}${notaBriefing ? ` | Briefing: "${notaBriefing}"` : ''}`,
          vecchioDos,
          { dosUscente: vecchioDos, dosEntrante: nuovoDos, briefing: notaBriefing }
        );
        set((s) => ({
          dosAttivo: nuovoDos,
          diarioOperativo: [...s.diarioOperativo, voce],
        }));
      },

      clearDiario: () => set({ diarioOperativo: [], dosAttivo: '', oraAttivazione: '' }),

      // Import/Export base
      exportData: () => {
        const state = get();
        const data = {
          coau: state.coau,
          coordinate: state.coordinate,
          squadLeaders: state.squadLeaders,
          personale: state.personale,
          mezziTerrestri: state.mezziTerrestri,
          mezziAerei: state.mezziAerei,
          droni: state.droni,
          laces: state.laces,
          messaggiRadio: state.messaggiRadio,
          media: state.media,
          logComunicazioni: state.logComunicazioni,
          diarioOperativo: state.diarioOperativo,
          dosAttivo: state.dosAttivo,
          exportDate: new Date().toISOString(),
          version: '1.0.0',
        };
        return JSON.stringify(data, null, 2);
      },

      importData: (json: string) => {
        try {
          const data = JSON.parse(json);
          set({
            coau: data.coau || null,
            coordinate: data.coordinate || [],
            squadLeaders: data.squadLeaders || [],
            personale: data.personale || [],
            mezziTerrestri: data.mezziTerrestri || [],
            mezziAerei: data.mezziAerei || [],
            droni: data.droni || [],
            laces: data.laces || null,
            messaggiRadio: data.messaggiRadio || [],
            media: data.media || [],
            logComunicazioni: data.logComunicazioni || [],
            diarioOperativo: data.diarioOperativo || [],
            dosAttivo: data.dosAttivo || '',
            oraAttivazione: data.oraAttivazione || '',
          });
        } catch (e) {
          console.error('Errore import dati:', e);
          
        }
      },

      // Export/Import completo con IndexedDB e ZIP
      exportDataComplete: async () => {
        const state = get();
        try {
          await exportImportManager.exportComplete(state);
          console.log('✅ Export completo eseguito');
        } catch (error) {
          console.error('❌ Errore export completo:', error);
          // error handled above
          throw error;
        }
      },

      importDataFile: async (file: File) => {
        try {
          const importedData = await exportImportManager.importComplete(file);
          set({
            coau: importedData.data.coau || null,
            coordinate: importedData.data.coordinate || [],
            squadLeaders: [],
            personale: importedData.data.risorse?.personale || [],
            mezziTerrestri: importedData.data.risorse?.mezziTerrestri || [],
            mezziAerei: importedData.data.risorse?.mezziAerei || [],
            droni: importedData.data.risorse?.droni || [],
            laces: importedData.data.laces || null,
            messaggiRadio: [],
            media: (importedData.data.media || []) as any,
            logComunicazioni: importedData.data.fraseologia || [],
          });
          console.log('✅ Import completo eseguito');
        } catch (error) {
          console.error('❌ Errore import completo:', error);
          // error handled above
          throw error;
        }
      },

      resetData: () => set(initialState),
    }),
    {
      name: 'dos518-storage',
      partialize: (state) => ({
        coau: state.coau,
        coordinate: state.coordinate,
        squadLeaders: state.squadLeaders,
        personale: state.personale,
        mezziTerrestri: state.mezziTerrestri,
        mezziAerei: state.mezziAerei,
        droni: state.droni,
        lookouts: state.lookouts,
        laces: state.laces,
        messaggiRadio: state.messaggiRadio,
        media: state.media,
        logComunicazioni: state.logComunicazioni,
        diarioOperativo: state.diarioOperativo,
        dosAttivo: state.dosAttivo,
        oraAttivazione: state.oraAttivazione,
      }),
    }
  )
);

// Selectors
export const useCoordinate = () => useAppStore((state) => state.coordinate);
export const useSquadLeaders = () => useAppStore((state) => state.squadLeaders);
export const usePersonale = () => useAppStore((state) => state.personale);
export const useMezziTerrestri = () => useAppStore((state) => state.mezziTerrestri);
export const useMezziAerei = () => useAppStore((state) => state.mezziAerei);
export const useDroni = () => useAppStore((state) => state.droni);
export const useCOAU = () => useAppStore((state) => state.coau);
export const useLACES = () => useAppStore((state) => state.laces);
export const useLogComunicazioni = () => useAppStore((state) => state.logComunicazioni);
export const useDiarioOperativo = () => useAppStore((state) => state.diarioOperativo);
export const useDosAttivo = () => useAppStore((state) => state.dosAttivo);
